import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, ExternalLink, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getGoogleMapsApiKey } from '@/lib/api-keys';
import { loadGoogleMapsApi, isGoogleMapsLoaded } from '@/lib/google-maps-loader';

interface LocationDetails {
  placeId: string;
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  googleMapsUrl: string;
}

interface LocationConfirmationSectionProps {
  isOpen: boolean;
  location: LocationDetails;
  onConfirm: (location: LocationDetails) => void;
  onCancel: () => void;
}

// Definimos um tipo para o CustomInfoOverlay que será criado dinamicamente
type CustomOverlayType = new (
  position: google.maps.LatLng,
  content: HTMLElement,
  hasPhoto: boolean
) => google.maps.OverlayView;

export function LocationConfirmationSection({
  isOpen,
  location,
  onConfirm,
  onCancel
}: LocationConfirmationSectionProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [placePhoto, setPlacePhoto] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const [mapsApiLoaded, setMapsApiLoaded] = useState(false);

  // Carregar a API do Google Maps quando o componente montar
  useEffect(() => {
    if (!isOpen) return;
    
    if (!isGoogleMapsLoaded()) {
      setIsLoading(true);
      loadGoogleMapsApi()
        .then(() => {
          setMapsApiLoaded(true);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Erro ao carregar a API do Google Maps:', error);
          setIsLoading(false);
        });
    } else {
      setMapsApiLoaded(true);
    }
  }, [isOpen]);

  // Função para criar a classe CustomInfoOverlay quando o Google Maps estiver disponível
  const createCustomOverlay = (): CustomOverlayType | null => {
    if (!window.google?.maps) return null;

    class CustomInfoOverlay extends google.maps.OverlayView {
      private position: google.maps.LatLng;
      private content: HTMLElement;
      private div: HTMLElement | null = null;
      private hasPhoto: boolean;

      constructor(position: google.maps.LatLng, content: HTMLElement, hasPhoto: boolean) {
        super();
        this.position = position;
        this.content = content;
        this.hasPhoto = hasPhoto;
      }

      // Chamado quando o overlay é adicionado ao mapa
      onAdd() {
        // Criar o elemento div que conterá o conteúdo
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.borderRadius = '6px';
        div.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
        div.style.overflow = 'hidden';
        div.style.zIndex = '1';
        div.style.transform = 'scale(0.8)'; // Reduzir o tamanho em 20%
        div.style.transformOrigin = 'bottom center'; // Manter o ponto de ancoragem na parte inferior central
        
        // Adicionar o conteúdo ao div
        div.appendChild(this.content);
        
        // Adicionar o div ao pane de sobreposição
        const panes = this.getPanes();
        panes?.overlayMouseTarget.appendChild(div);
        
        this.div = div;
      }

      // Chamado quando a posição do overlay precisa ser atualizada
      draw() {
        if (!this.div) return;
        
        // Projetar a posição geográfica para coordenadas de pixel
        const projection = this.getProjection();
        const point = projection.fromLatLngToDivPixel(this.position);
        
        if (point) {
          // Calcular a altura do conteúdo para posicionamento adequado
          const contentHeight = this.div.offsetHeight || 176; // 220 * 0.8 = 176
          
          // Posicionar o div no mapa (centralizado horizontalmente, acima do marcador)
          this.div.style.left = (point.x - 112) + 'px'; // 280 * 0.8 / 2 = 112px
          
          // Posicionar acima do marcador com um deslocamento adicional para o pin
          // O valor 32 representa aproximadamente a altura do pin do marcador
          this.div.style.top = (point.y - contentHeight - 32) + 'px';
          
          // Adicionar uma pequena seta apontando para o pin
          const arrow = document.createElement('div');
          arrow.style.position = 'absolute';
          arrow.style.bottom = '-8px';
          arrow.style.left = '50%';
          arrow.style.marginLeft = '-8px';
          arrow.style.width = '0';
          arrow.style.height = '0';
          arrow.style.borderLeft = '8px solid transparent';
          arrow.style.borderRight = '8px solid transparent';
          arrow.style.borderTop = this.hasPhoto ? '8px solid white' : '8px solid #f0f0f0';
          
          // Adicionar a seta apenas se ainda não existir
          if (!this.div.querySelector('.map-overlay-arrow')) {
            arrow.className = 'map-overlay-arrow';
            this.div.appendChild(arrow);
          }
        }
      }

      // Chamado quando o overlay é removido do mapa
      onRemove() {
        if (this.div) {
          this.div.parentNode?.removeChild(this.div);
          this.div = null;
        }
      }
    }

    return CustomInfoOverlay;
  };

  // Buscar foto do local
  useEffect(() => {
    if (!isOpen || !mapsApiLoaded || !location.placeId) return;

    const fetchPlacePhoto = async () => {
      try {
        // Tentar obter a foto usando a Places API v1
        const apiKey = getGoogleMapsApiKey();
        if (!apiKey) return;

        const url = `https://places.googleapis.com/v1/places/${location.placeId}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': 'photos',
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.photos && data.photos.length > 0) {
            try {
              // Construir URL da foto
              const photoReference = data.photos[0].name;
              // Usando referência direta com proxy CORS para garantir carregamento da imagem
              const photoUrl = `https://places.googleapis.com/v1/${photoReference}/media?key=${apiKey}&maxHeightPx=200&maxWidthPx=400`;
              
              // Testar se a imagem está disponível
              const img = new Image();
              img.crossOrigin = "anonymous";
              img.onload = () => {
                setPlacePhoto(photoUrl);
              };
              img.onerror = () => {
                // Se falhar, tenta o fallback
                tryFallbackPhoto();
              };
              img.src = photoUrl;
              return;
            } catch (imgError) {
              console.error('Erro ao carregar imagem da Places API v1:', imgError);
              tryFallbackPhoto();
            }
          } else {
            tryFallbackPhoto();
          }
        } else {
          tryFallbackPhoto();
        }
      } catch (error) {
        console.error('Erro ao buscar foto do local:', error);
        tryFallbackPhoto();
      }
    };

    // Função para tentar obter foto usando o método alternativo
    const tryFallbackPhoto = () => {
      // Fallback: usar a API JavaScript
      if (window.google?.maps?.places) {
        try {
          const mapDiv = document.createElement('div');
          const map = new window.google.maps.Map(mapDiv);
          const placesService = new window.google.maps.places.PlacesService(map);
          
          placesService.getDetails(
            {
              placeId: location.placeId,
              fields: ['photos']
            },
            (place, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.photos?.length) {
                const photoUrl = place.photos[0].getUrl({ maxWidth: 400, maxHeight: 200 });
                setPlacePhoto(photoUrl);
              }
            }
          );
        } catch (fallbackError) {
          console.error('Erro no fallback para obter foto:', fallbackError);
        }
      }
    };

    fetchPlacePhoto();
  }, [isOpen, location.placeId, mapsApiLoaded]);

  // Inicializar o mapa quando o componente montar
  useEffect(() => {
    if (!isOpen || !mapRef.current || !mapsApiLoaded) return;

    try {
      setIsLoading(true);

      // Criar o mapa
      const map = new window.google.maps.Map(mapRef.current, {
        center: location.coordinates,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });

      // Criar o marcador
      const newMarker = new window.google.maps.Marker({
        position: location.coordinates,
        map: map,
        title: location.name
      });

      // Criar o conteúdo HTML para o overlay personalizado
      const content = document.createElement('div');
      content.innerHTML = `
        <div style="width: 224px; background-color: white; overflow: hidden; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
          <div style="padding: 8px; background-color: #0D9488; color: white; font-size: 12px; font-weight: 600; text-align: center;">
            ${location.name}
          </div>
          ${placePhoto ? 
            `<div style="width: 100%; height: 112px; overflow: hidden; position: relative;">
              <img src="${placePhoto}" alt="${location.name}" style="width: 100%; height: 100%; object-fit: cover; display: block;" crossorigin="anonymous" />
            </div>` : 
            `<div style="width: 100%; height: 64px; background-color: #f0f0f0; display: flex; align-items: center; justify-content: center;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" fill="#0D9488"/>
                <path d="M12 2C7.6 2 4 5.4 4 9.5C4 14.27 8.04 19.22 11.06 21.79C11.35 22.03 11.67 22.15 12 22.15C12.33 22.15 12.65 22.03 12.94 21.79C15.96 19.22 20 14.27 20 9.5C20 5.4 16.4 2 12 2ZM12 11.5C11.17 11.5 10.5 10.83 10.5 10C10.5 9.17 11.17 8.5 12 8.5C12.83 8.5 13.5 9.17 13.5 10C13.5 10.83 12.83 11.5 12 11.5Z" fill="#0D9488"/>
              </svg>
            </div>`
          }
          <div style="padding: 8px; font-size: 11px; color: #666;">
            ${location.address ? location.address.split(',').slice(0, 2).join(',') : ''}
          </div>
        </div>
      `;

      // Criar e adicionar o overlay personalizado
      const CustomOverlay = createCustomOverlay();
      if (CustomOverlay) {
        const overlay = new CustomOverlay(
          new google.maps.LatLng(location.coordinates.lat, location.coordinates.lng),
          content,
          !!placePhoto
        );
        overlay.setMap(map);
        overlayRef.current = overlay;
      }

      // Armazenar referências
      mapInstance.current = map;
      marker.current = newMarker;

      // Trigger resize para garantir que o mapa renderize corretamente
      window.google.maps.event.trigger(map, 'resize');
    } catch (error) {
      console.error('Erro ao inicializar o mapa:', error);
    } finally {
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
      if (marker.current) {
        marker.current.setMap(null);
        marker.current = null;
      }
      mapInstance.current = null;
    };
  }, [isOpen, location, placePhoto, mapsApiLoaded]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay escuro para destacar o mapa */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onCancel}></div>
      
      {/* Container do mapa */}
      <div className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 max-w-4xl z-50 bg-white rounded-md shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-medium text-voah-teal">Confirmar localização</h3>
            <button 
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Fechar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div className="mb-4">
            <div className="flex items-center text-gray-700 mb-2">
              <MapPin className="h-5 w-5 mr-2 text-voah-teal" />
              <span className="font-medium">{location.name}</span>
            </div>
            {location.address && (
              <div className="text-sm text-gray-600 ml-7">
                {location.address}
              </div>
            )}
          </div>
          
          <div 
            ref={mapRef} 
            className="w-full h-[400px] bg-gray-100 rounded-md relative"
          >
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                <Loader2 className="h-8 w-8 animate-spin text-voah-teal" />
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button 
              variant="outline" 
              onClick={onCancel}
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => onConfirm(location)}
              className="bg-voah-teal hover:bg-voah-teal-dark text-white"
            >
              Confirmar Localização
            </Button>
          </div>
        </div>
      </div>
    </>
  );
} 